'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { useMemo, type FC } from 'react'
import type { FieldErrors } from 'react-hook-form'
import { useForm } from 'react-hook-form'

import {
  getListUsersQueryKey,
  useGetDiscountTiers,
  useGetUserEditInformation,
  useListAccountManagers,
  useUpdateUser,
  type UserControls,
  type UserEditResponse,
  type UserUpdateRequestBodyUserBlockedPayoutMethodsItem,
  type UserUpdateResponse,
} from '@api/orval/apiHooks'
import { useOpenCustomActionDialog } from '@components/DynamicActions/hooks/useOpenCustomActionDialog'
import type { Action } from '@components/DynamicActions/types'
import {
  CheckboxField,
  FormGrid,
  FormSection,
  MultiSelectField,
  SingleSelectField,
  TextInputField,
} from '@components/Form/FormFields'
import { normalizeSelectOption } from '@components/Form/formHelpers'
import { useConditionalFields } from '@components/Form/useConditionalFields'
import { Sheet, SheetContent, SheetTitle } from '@components/ui/Sheet'
import { Button, Spin } from '@fp/ui-kit'
import { emitToast } from '@lib/emitToast'
import { getUserFormSchema, type UserFormValues } from '@lib/formSchemas'
import { PAYOUT_LABEL, PAYOUTS_LABEL } from '@lib/payoutTerminology'

const UNASSIGNED_ACCOUNT_MANAGER_VALUE = '__unassigned_account_manager__'
export const UNASSIGNED_ACCOUNT_MANAGER_LABEL = 'Unassigned'
export const ACCOUNT_MANAGER_LIST_PARAMS = { limit: 1000, field: 'email', direction: 'asc' } as const
export const INHERITED_CONTROL_VALUE = '__inherited_control__'

// Controls are rendered from whatever the backend returns, so a control added there needs no
// release here. That rules out hand-written labels — the key is the label.
const humanizeControl = (key: string) => key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())

export const CONTROL_OPTIONS = [
  { label: 'Inherited', value: INHERITED_CONTROL_VALUE },
  { label: 'Allowed', value: 'true' },
  { label: 'Blocked', value: 'false' },
]

const CHECKBOX_FIELDS: { name: keyof UserFormValues; label: string }[] = [
  { name: 'paid_customer', label: 'Affiliate enabled (paid customer)' },
  { name: 'affiliate_payouts_on_demand_enabled', label: `Affiliate ${PAYOUTS_LABEL} on Demand` },
  { name: 'competition_manager', label: 'Competition Manager' },
  { name: 'can_be_account_manager', label: 'Can Be Account Manager' },
]

const getAccountManagerLabel = ({
  email,
  first_name,
  last_name,
}: {
  email: string
  first_name?: string | null
  last_name?: string | null
}) => {
  const fullName = [first_name, last_name].filter(Boolean).join(' ').trim()

  return fullName ? `${fullName} (${email})` : email
}

// A control the user does not override comes back as null, which the select carries as its own
// option rather than as an empty value — otherwise "inherited" and "not yet chosen" look alike.
const toControlFormValues = (controls: UserControls | undefined) =>
  Object.fromEntries(
    Object.entries(controls ?? {}).map(([key, value]) => [
      key,
      value == null ? INHERITED_CONTROL_VALUE : String(value),
    ]),
  )

// Only the controls touched in this edit are sent. The backend leaves omitted controls alone, so
// submitting the whole set would let a stale form clear an override another admin set meanwhile.
const toControlsPayload = (
  controls: UserFormValues['controls'],
  changed: Partial<Record<string, boolean>> | undefined,
): UserControls =>
  Object.fromEntries(
    Object.entries(controls ?? {})
      .filter(([key]) => changed?.[key])
      .map(([key, value]) => [key, value === INHERITED_CONTROL_VALUE ? null : value === 'true']),
  )

// Non-form keys spread in here (id, *_options, actions, …) are stripped by zod on submit.
const toFormValues = ({
  account_manager,
  permission_group_ids,
  controls,
  ...user
}: UserEditResponse): UserFormValues => ({
  ...user,
  account_manager_id: account_manager ? String(account_manager.id) : UNASSIGNED_ACCOUNT_MANAGER_VALUE,
  permission_group_ids: (permission_group_ids ?? []).map(String),
  controls: toControlFormValues(controls),
})

type EditUserSheetProps = {
  isOpenOutside?: boolean
  onOpenChange?: (open: boolean) => void
} & Omit<EditUserFormProps, 'onCancel'>

export const EditUserSheet: FC<EditUserSheetProps> = ({ userId, onSuccess, isOpenOutside, onOpenChange }) => {
  const { isOpen, handleOpenChange, handleClose } = useOpenCustomActionDialog(isOpenOutside, onOpenChange)

  return (
    <Sheet open={isOpen} onOpenChange={handleOpenChange}>
      <SheetContent className='sm:max-w-2xl overflow-y-auto'>
        <SheetTitle className='mb-4'>Edit User</SheetTitle>

        {typeof userId !== 'undefined' && <EditUserForm userId={userId} onCancel={handleClose} onSuccess={onSuccess} />}
      </SheetContent>
    </Sheet>
  )
}

interface EditUserFormProps {
  userId: number
  onCancel: () => void
  onSuccess?: (res?: UserUpdateResponse, currentAction?: Action) => void
}

export const EditUserForm = ({ userId, onCancel, onSuccess }: EditUserFormProps) => {
  const qc = useQueryClient()

  const { data: editData, isPending: isFetching } = useGetUserEditInformation(userId, {
    query: {
      enabled: typeof userId === 'number',
    },
  })

  const { isEditable } = useConditionalFields({
    editableFields: editData?.editable_fields as string[],
  })

  const isEmailEditable = isEditable('email')
  const isControlsEditable = isEditable('controls')

  const resolver = useMemo(() => zodResolver(getUserFormSchema(isEmailEditable)), [isEmailEditable])

  const {
    handleSubmit,
    control,
    formState: { errors, dirtyFields },
  } = useForm<UserFormValues>({
    resolver,
    values: editData && toFormValues(editData),
  })

  const isDiscountTierEditable = isEditable('discount_tier_id')

  // `UserEditResponse` carries no `discount_tier_options`, so affiliate tiers are read straight from
  // the discount tiers list. Campaign tiers are not assignable to a user.
  const { data: discountTiersData } = useGetDiscountTiers(
    { type: 'affiliate', limit: 1000, field: 'name', direction: 'asc' },
    { query: { enabled: isDiscountTierEditable } },
  )

  const discountTierOptions = useMemo(
    () =>
      (discountTiersData?.collection ?? []).map((tier) => normalizeSelectOption({ label: tier.name, value: tier.id })),
    [discountTiersData?.collection],
  )

  const { data: accountManagersData, isPending: isFetchingAccountManagers } = useListAccountManagers(
    ACCOUNT_MANAGER_LIST_PARAMS,
    {
      query: {
        enabled: isEditable('account_manager_id'),
      },
    },
  )

  const { mutateAsync: updateUserMutate, isPending: isLoading } = useUpdateUser()

  const assignedAccountManager = editData?.account_manager

  const accountManagerOptions = useMemo(() => {
    const managers = new Map(
      [...(assignedAccountManager ? [assignedAccountManager] : []), ...(accountManagersData?.collection ?? [])].map(
        (manager) => [manager.id, manager],
      ),
    )

    return [
      { label: UNASSIGNED_ACCOUNT_MANAGER_LABEL, value: UNASSIGNED_ACCOUNT_MANAGER_VALUE },
      ...[...managers.values()].map((manager) => ({
        label: getAccountManagerLabel(manager),
        value: String(manager.id),
      })),
    ]
  }, [assignedAccountManager, accountManagersData?.collection])

  const onFinish = async ({ account_manager_id, permission_group_ids, email, controls, ...values }: UserFormValues) => {
    const changedControls = toControlsPayload(controls, dirtyFields.controls)

    const payload = {
      ...values,
      ...(isEmailEditable && { email }),
      ...(isControlsEditable && Object.keys(changedControls).length > 0 && { controls: changedControls }),
      account_manager_id:
        account_manager_id && account_manager_id !== UNASSIGNED_ACCOUNT_MANAGER_VALUE
          ? Number(account_manager_id)
          : null,
      blocked_payout_methods: values.blocked_payout_methods as UserUpdateRequestBodyUserBlockedPayoutMethodsItem[],
      permission_group_ids: permission_group_ids?.map(Number),
      // Orval generates from one spec where the tier ids are mandatory; Tradin accepts null.
      discount_tier_id: values.discount_tier_id as number,
      // Null outside the referral program; the backend ignores a blank code.
      referral_code: values.referral_code ?? '',
    }

    try {
      const res = await updateUserMutate({ id: userId, data: { user: payload } })
      qc.invalidateQueries({ queryKey: getListUsersQueryKey() })
      emitToast({ message: 'User updated successfully', type: 'success' })
      onSuccess?.(res)
      onCancel()
    } catch {
      console.warn('Something went wrong, please try again.')
    }
  }

  const onInvalid = (invalidFields: FieldErrors<UserFormValues>) => {
    const hasHiddenFieldError = Object.keys(invalidFields).some((field) => !isEditable(field))

    emitToast({
      message: hasHiddenFieldError
        ? 'This user has an invalid value in a field you cannot edit. Contact an admin to fix it.'
        : 'Please fix the highlighted fields before submitting.',
      type: 'error',
    })
  }

  if (isFetching) {
    return (
      <div className='flex justify-center items-center min-h-[400px]'>
        <Spin size='lg' />
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit(onFinish, onInvalid)} className='space-y-6'>
      {(isEditable('role') || isEditable('permission_group_ids')) && (
        <FormSection className='rounded-lg border border-border bg-muted/40 p-4'>
          <p className='text-sm font-semibold text-foreground'>Access Control</p>

          {isEditable('role') && (
            <SingleSelectField
              name='role'
              label='User Role'
              options={editData?.role_options.map(normalizeSelectOption) || []}
              control={control}
              error={errors.role?.message}
              modal
            />
          )}

          {isEditable('permission_group_ids') && (
            <MultiSelectField
              name='permission_group_ids'
              label='Permission Groups'
              options={(editData?.permission_group_options ?? []).map((o) => ({
                label: o.label ?? '',
                value: String(o.value ?? ''),
              }))}
              control={control}
              error={errors.permission_group_ids?.message}
              placeholder='Select permission groups'
              modal
            />
          )}
        </FormSection>
      )}

      {isControlsEditable && (
        <FormSection className='rounded-lg border border-border bg-muted/40 p-4'>
          <p className='text-sm font-semibold text-foreground'>Controls</p>
          <p className='text-xs text-muted-foreground'>
            Inherited falls back to the user&apos;s country, brand and the global setting, in that order.
          </p>

          <FormGrid>
            {Object.keys(editData?.controls ?? {}).map((name) => (
              <SingleSelectField
                key={name}
                name={`controls.${name}`}
                label={humanizeControl(name)}
                options={CONTROL_OPTIONS}
                control={control}
                isToggleEnabled={false}
                modal
              />
            ))}
          </FormGrid>
        </FormSection>
      )}

      <FormSection>
        <FormGrid>
          {isEditable('first_name') && (
            <TextInputField
              name='first_name'
              label='First Name'
              control={control}
              error={errors.first_name?.message}
              required
            />
          )}
          {isEditable('last_name') && (
            <TextInputField
              name='last_name'
              label='Last Name'
              control={control}
              error={errors.last_name?.message}
              required
            />
          )}
        </FormGrid>

        <FormGrid>
          {isDiscountTierEditable && (
            <SingleSelectField
              name='discount_tier_id'
              label='Discount Tier'
              options={discountTierOptions}
              control={control}
              error={errors.discount_tier_id?.message}
              modal
            />
          )}
          {isEmailEditable && (
            <TextInputField name='email' label='Email' control={control} error={errors.email?.message} required />
          )}
        </FormGrid>

        {isEditable('account_manager_id') && (
          <SingleSelectField
            name='account_manager_id'
            label='Account Manager'
            options={accountManagerOptions}
            control={control}
            error={errors.account_manager_id?.message}
            disabled={isFetchingAccountManagers}
            isToggleEnabled={false}
            modal
            placeholder={isFetchingAccountManagers ? 'Loading account managers...' : 'Select account manager'}
          />
        )}

        <FormGrid>
          {isEditable('referral_code') && (
            <TextInputField
              name='referral_code'
              label='Referral Code'
              control={control}
              error={errors.referral_code?.message}
            />
          )}
          {isEditable('phone_number_verification_status') && (
            <SingleSelectField
              name='phone_number_verification_status'
              label='Phone Verification Status'
              options={editData?.phone_number_verification_status_options.map(normalizeSelectOption) || []}
              control={control}
              error={errors.phone_number_verification_status?.message}
              required
            />
          )}
        </FormGrid>

        {isEditable('blocked_payout_methods') && (
          <MultiSelectField
            name='blocked_payout_methods'
            label={`Blocked ${PAYOUT_LABEL} Methods`}
            options={editData?.blocked_payout_method_options ?? []}
            control={control}
            error={errors.blocked_payout_methods?.message}
            placeholder={`Select blocked ${PAYOUT_LABEL.toLowerCase()} methods`}
            modal
          />
        )}

        <FormGrid>
          {isEditable('max_active_accounts') && (
            <TextInputField
              name='max_active_accounts'
              label='Total Active Accounts'
              type='number'
              min={0}
              control={control}
              error={errors.max_active_accounts?.message}
            />
          )}
          {isEditable('max_active_allocation') && (
            <TextInputField
              name='max_active_allocation'
              label='Total Max Allocation'
              type='number'
              min={0}
              control={control}
              error={errors.max_active_allocation?.message}
            />
          )}
        </FormGrid>

        {isEditable('kycaid_applicant_identifier') && (
          <TextInputField
            name='kycaid_applicant_identifier'
            label='KYCAID Identifier'
            control={control}
            error={errors.kycaid_applicant_identifier?.message}
          />
        )}
      </FormSection>

      <FormSection className='space-y-4'>
        {CHECKBOX_FIELDS.filter(({ name }) => isEditable(name)).map(({ name, label }) => (
          <CheckboxField key={name} name={name} label={label} control={control} />
        ))}
      </FormSection>

      <Button type='submit' variant='primary' size='block' isLoading={isLoading} disabled={isLoading}>
        Submit
      </Button>
    </form>
  )
}
